export type PriorityKey = 'A' | 'B' | 'C' | 'D' | 'F'

// Shared order for priorities across the app
export const PRIORITIES: readonly PriorityKey[] = ['A', 'B', 'C', 'D', 'F'] as const

/**
 * The letters a picker offers. `F` is a legacy letter the server refuses on
 * write: a task that carries it still shows it (every map here reads the full
 * scale), but no control may send it (MYAC-1661).
 */
export const WRITABLE_PRIORITIES = ['A', 'B', 'C', 'D'] as const satisfies readonly PriorityKey[]

export const PRIORITY_COLORS: Record<PriorityKey, string> = {
    A: '#B71C1C', // red darken3
    B: '#FF5722', // deepOrange base
    C: '#FB8C00', // orange darken1
    D: '#03A9F4', // lightBlue base
    F: '#1565C0', // blue darken3
}

// PrimeIcons classes for each priority
export const PRIORITY_ICONS: Record<PriorityKey, string> = {
    A: 'pi pi-angle-double-up',
    B: 'pi pi-angle-up',
    C: 'pi pi-minus',
    D: 'pi pi-angle-down',
    F: 'pi pi-angle-double-down',
}

// Rank (higher is more critical/important). Useful for sorting.
export const PRIORITY_RANK: Record<PriorityKey, number> = {
    A: 5,
    B: 4,
    C: 3,
    D: 2,
    F: 1,
}

export function comparePriorityDesc(a: PriorityKey, b: PriorityKey): number {
    return PRIORITY_RANK[b] - PRIORITY_RANK[a]
}

export function isPriorityKey(value: unknown): value is PriorityKey {
    return typeof value === 'string' && (PRIORITIES as readonly string[]).includes(value)
}

// i18n keys for urgency labels displayed in the Action-First UI
export const PRIORITY_URGENCY_KEYS: Record<PriorityKey, string> = {
    A: 'task.urgency.urgent',
    B: 'task.urgency.high',
    C: 'task.urgency.medium',
    D: 'task.urgency.low',
    F: 'task.urgency.minor',
}

// PrimeVue Tag severity for urgency badges
export const PRIORITY_URGENCY_SEVERITY: Record<PriorityKey, string> = {
    A: 'danger',
    B: 'warn',
    C: 'info',
    D: 'secondary',
    F: 'secondary',
}

/** Map from task.priority string values to PriorityKey letter codes */
export const TASK_PRIORITY_TO_KEY: Record<string, PriorityKey> = {
    urgent: 'A',
    high: 'B',
    medium: 'C',
    low: 'D',
}

/** Sort weight for kanban column ordering (lower = higher priority = appears first).
 *  Supports both string form (urgent/high/medium/low) and letter codes (A-F)
 *  because the TaskDetail dialog persists letters while assessment imports
 *  may still use strings. Keep both in sync with TASK_PRIORITY_TO_KEY. */
export const TASK_PRIORITY_SORT_WEIGHT: Record<string, number> = {
    urgent: 0, A: 0,
    high: 1, B: 1,
    medium: 2, C: 2,
    low: 3, D: 3,
    F: 4,
}

/** Get sort weight for a task priority string, defaulting to lowest priority */
export function getTaskPrioritySortWeight(priority: string | undefined): number {
    return TASK_PRIORITY_SORT_WEIGHT[priority ?? ''] ?? 99
}

/** Tailwind dot classes for task priority — matches PrimeVue Tag severity colors */
export const TASK_PRIORITY_DOT_CLASS: Record<string, string> = {
    urgent: 'bg-red-500',
    high: 'bg-orange-500',
    medium: 'bg-sky-500',
    low: 'bg-gray-500',
    A: 'bg-red-500',
    B: 'bg-orange-500',
    C: 'bg-sky-500',
    D: 'bg-gray-500',
    F: 'bg-gray-400',
}

/** Get Tailwind dot class for a task priority string or letter code */
export function getTaskPriorityDotClass(priority: string): string {
    return TASK_PRIORITY_DOT_CLASS[priority] ?? 'bg-surface-300'
}

/**
 * Subject priority (critical / high / medium) mapped onto the existing
 * task priority scale (A / B / C) for visual consistency.
 */
export const SUBJECT_PRIORITY_TO_TASK_KEY: Record<'critical' | 'high' | 'medium', PriorityKey> = {
    critical: 'A',
    high: 'B',
    medium: 'C',
}

export function getSubjectPriorityDotClass(priority: 'critical' | 'high' | 'medium'): string {
    return getTaskPriorityDotClass(SUBJECT_PRIORITY_TO_TASK_KEY[priority])
}

export function getSubjectPriorityI18nKey(priority: 'critical' | 'high' | 'medium'): string {
    return `assessments.summary.subject_priority.${priority}`
}
